/**
 * Admin: Request Types catalog editor.
 *
 * Lists all RequestType rows + lets admins create / edit / archive. Provides
 * a friendly "Fields" builder for the `data_schema` JSON column so admins
 * don't have to hand-write JSON descriptors.
 */

import { ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, GripVertical, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────

type SchemaField = {
  name: string
  label: string
  type: 'text' | 'textarea' | 'number' | 'date' | 'select'
  required?: boolean
  options?: string[]
  default?: string | number
  placeholder?: string
  help?: string
}

type RequestType = {
  id: number
  code: string
  name: string
  description: string
  icon: string
  data_schema: SchemaField[]
  default_workflow: number | null
  default_workflow_code: string | null
  fulfiller_role: number | null
  fulfiller_role_name: string | null
  fulfiller_person: number | null
  fulfiller_person_name: string | null
  is_active: boolean
  display_order: number
}

type WorkflowDef = { id: number; code: string; name: string; is_active: boolean }
type Role = { id: number; name: string }
type Person = { id: number; full_name: string }

const TYPE_OPTIONS: Array<{ value: SchemaField['type']; label: string }> = [
  { value: 'text', label: 'Single-line text' },
  { value: 'textarea', label: 'Multi-line text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
]

// ─────────────────── Page ───────────────────

export default function RequestTypesAdmin() {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()

  const typesQ = useQuery({
    queryKey: ['requests', 'types'],
    queryFn: async () => (await api.get<RequestType[]>('/request-types/')).data,
  })
  const wfQ = useQuery({
    queryKey: ['requests', 'workflows'],
    queryFn: async () => (await api.get<WorkflowDef[]>('/workflows/')).data,
  })
  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await api.get<Role[]>('/roles/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<Person[]>('/hr/persons/')).data,
  })

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<RequestType | null>(null)

  const saveMut = useMutation({
    mutationFn: async (payload: Partial<RequestType>) => {
      if (editing) {
        return (await api.patch(`/request-types/${editing.id}/`, payload)).data
      }
      return (await api.post('/request-types/', payload)).data
    },
    onSuccess: () => {
      toast.success(editing ? 'Type updated' : 'Type created')
      qc.invalidateQueries({ queryKey: ['requests', 'types'] })
      setDrawerOpen(false)
      setEditing(null)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/request-types/${id}/`),
    onSuccess: () => {
      toast.success('Type deleted')
      qc.invalidateQueries({ queryKey: ['requests', 'types'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const types = (typesQ.data || []).slice().sort((a, b) => a.display_order - b.display_order)
  const workflows = (wfQ.data || []).filter((w) => w.is_active)

  return (
    <AdminLayout>
      {dialog}

      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs">
            <Link to="/admin/requests" className="text-muted-foreground hover:text-foreground hover:underline">
              ← My Requests
            </Link>
          </div>
          <h2 className="text-2xl font-semibold text-foreground tracking-tight mt-2">Request Types</h2>
          <p className="text-sm text-muted-foreground mt-1">
            The catalog employees pick from when submitting a request. Each type defines its own fields and the
            workflow approvals follow.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setDrawerOpen(true) }}>
          <Plus className="h-3 w-3 mr-1" /> New type
        </Button>
      </div>

      {typesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : types.length === 0 ? (
        <div className="bg-card rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          No request types yet.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Fields</Th>
                <Th>Workflow</Th>
                <Th>Fulfiller</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {types.map((t) => (
                <tr key={t.id} className="hover:bg-muted/30">
                  <Td className="font-mono text-xs">{t.code}</Td>
                  <Td className="text-sm">
                    <span className="mr-1">{t.icon}</span>
                    <span className="font-medium">{t.name}</span>
                  </Td>
                  <Td className="text-xs text-muted-foreground">
                    {(t.data_schema || []).length} field{(t.data_schema || []).length === 1 ? '' : 's'}
                  </Td>
                  <Td className="text-xs">
                    {t.default_workflow_code || <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-xs">
                    {t.fulfiller_person_name
                      || t.fulfiller_role_name
                      || <span className="text-muted-foreground">auto-close</span>}
                  </Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${t.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {t.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </Td>
                  <Td className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditing(t); setDrawerOpen(true) }}>
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => confirm(`type ${t.code}`, () => deleteMut.mutate(t.id))}
                        >Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawerOpen && (
        <TypeDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          initial={editing}
          workflows={workflows}
          roles={rolesQ.data || []}
          persons={personsQ.data || []}
          onSubmit={(p) => saveMut.mutate(p)}
          isPending={saveMut.isPending}
        />
      )}
    </AdminLayout>
  )
}

// ─────────────────── Drawer ───────────────────

function TypeDrawer({
  open, onOpenChange, initial, workflows, roles, persons, onSubmit, isPending,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  initial: RequestType | null
  workflows: WorkflowDef[]
  roles: Role[]
  persons: Person[]
  onSubmit: (payload: Partial<RequestType>) => void
  isPending: boolean
}) {
  const [code, setCode] = useState(initial?.code || '')
  const [name, setName] = useState(initial?.name || '')
  const [description, setDescription] = useState(initial?.description || '')
  const [icon, setIcon] = useState(initial?.icon || '')
  const [isActive, setIsActive] = useState(initial?.is_active ?? true)
  const [displayOrder, setDisplayOrder] = useState(initial?.display_order ?? 100)
  const [workflowId, setWorkflowId] = useState<string>(
    initial?.default_workflow ? String(initial.default_workflow) : '',
  )
  const [fulfillerRoleId, setFulfillerRoleId] = useState<string>(
    initial?.fulfiller_role ? String(initial.fulfiller_role) : '',
  )
  const [fulfillerPersonId, setFulfillerPersonId] = useState<string>(
    initial?.fulfiller_person ? String(initial.fulfiller_person) : '',
  )
  const [fields, setFields] = useState<SchemaField[]>(initial?.data_schema || [])

  useEffect(() => {
    if (!open) return
    setCode(initial?.code || '')
    setName(initial?.name || '')
    setDescription(initial?.description || '')
    setIcon(initial?.icon || '')
    setIsActive(initial?.is_active ?? true)
    setDisplayOrder(initial?.display_order ?? 100)
    setWorkflowId(initial?.default_workflow ? String(initial.default_workflow) : '')
    setFulfillerRoleId(initial?.fulfiller_role ? String(initial.fulfiller_role) : '')
    setFulfillerPersonId(initial?.fulfiller_person ? String(initial.fulfiller_person) : '')
    setFields(initial?.data_schema || [])
  }, [open, initial])

  function updateField(i: number, patch: Partial<SchemaField>) {
    setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)))
  }
  function addField() {
    setFields((prev) => [
      ...prev,
      { name: `field_${prev.length + 1}`, label: 'New field', type: 'text', required: false },
    ])
  }
  function removeField(i: number) {
    setFields((prev) => prev.filter((_, idx) => idx !== i))
  }
  function moveField(i: number, dir: -1 | 1) {
    setFields((prev) => {
      const target = i + dir
      if (target < 0 || target >= prev.length) return prev
      const next = prev.slice()
      ;[next[i], next[target]] = [next[target], next[i]]
      return next
    })
  }

  function submit() {
    if (!code.trim() || !name.trim()) {
      toast.error('Code and name are required.')
      return
    }
    onSubmit({
      code: code.trim().toUpperCase(),
      name: name.trim(),
      description,
      icon,
      is_active: isActive,
      display_order: displayOrder,
      default_workflow: workflowId ? Number(workflowId) : null,
      fulfiller_role: fulfillerRoleId ? Number(fulfillerRoleId) : null,
      fulfiller_person: fulfillerPersonId ? Number(fulfillerPersonId) : null,
      data_schema: fields,
    })
  }

  return (
    <RecordDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={initial ? `Edit type — ${initial.name}` : 'New request type'}
      description="Define what employees see when they pick this from the catalog: the form fields, the approval chain, and who fulfills it."
      onSubmit={submit}
      isPending={isPending}
      submitLabel={initial ? 'Save changes' : 'Create type'}
      width="wide"
    >
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="rt-code">Code *</FieldLabel>
          <Input
            id="rt-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="EXPENSE / TRAVEL / HARDWARE"
            disabled={!!initial}
          />
          <FieldDescription>
            Stable identifier — used in URLs and API calls. Can't change after creation.
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor="rt-name">Name *</FieldLabel>
          <Input id="rt-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field>
          <FieldLabel htmlFor="rt-icon">Icon</FieldLabel>
          <Input id="rt-icon" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="✈️ 💰 🖥️ …" maxLength={8} />
          <FieldDescription>One emoji shown on the catalog tile.</FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor="rt-desc">Description</FieldLabel>
          <Textarea
            id="rt-desc"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Shown under the tile and on the submit form."
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="rt-wf">Approval workflow *</FieldLabel>
          <SearchableSelect
            id="rt-wf"
            value={workflowId || NONE}
            onChange={(v) => setWorkflowId(v === NONE ? '' : v)}
            placeholder="— pick a workflow —"
            options={[
              { value: NONE, label: '— none —' },
              ...workflows.map((w) => ({ value: String(w.id), label: w.name, hint: w.code })),
            ]}
          />
          <FieldDescription>
            Workflows are defined under Settings → Workflows. New requests of this type snapshot the workflow at submit.
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor="rt-role">Fulfiller role</FieldLabel>
          <SearchableSelect
            id="rt-role"
            value={fulfillerRoleId || NONE}
            onChange={(v) => setFulfillerRoleId(v === NONE ? '' : v)}
            placeholder="— none —"
            options={[
              { value: NONE, label: '— none —' },
              ...roles.map((r) => ({ value: String(r.id), label: r.name })),
            ]}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="rt-person">Fulfiller person (overrides role)</FieldLabel>
          <SearchableSelect
            id="rt-person"
            value={fulfillerPersonId || NONE}
            onChange={(v) => setFulfillerPersonId(v === NONE ? '' : v)}
            placeholder="— none —"
            options={[
              { value: NONE, label: '— none —' },
              ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
            ]}
          />
          <FieldDescription>
            If both role and person are blank, requests auto-close after final approval.
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor="rt-order">Display order</FieldLabel>
          <Input
            id="rt-order"
            type="number"
            value={String(displayOrder)}
            onChange={(e) => setDisplayOrder(Number(e.target.value || 0))}
          />
          <FieldDescription>Lower numbers show first on the catalog.</FieldDescription>
        </Field>
        <Field>
          <FieldLabel>Active</FieldLabel>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Show in the catalog
          </label>
        </Field>

        {/* Field builder */}
        <Field>
          <FieldLabel>Form fields</FieldLabel>
          <FieldDescription>
            What the submit form asks the employee for. The values land in the request's <code>data</code> blob.
          </FieldDescription>
          <div className="space-y-2 mt-2">
            {fields.length === 0 && (
              <p className="text-xs text-muted-foreground">No fields yet. Click + to add one.</p>
            )}
            {fields.map((f, i) => (
              <SchemaFieldEditor
                key={i}
                field={f}
                onChange={(patch) => updateField(i, patch)}
                onRemove={() => removeField(i)}
                onUp={() => moveField(i, -1)}
                onDown={() => moveField(i, 1)}
              />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addField}>
              <Plus className="h-3 w-3 mr-1" /> Add field
            </Button>
          </div>
        </Field>
      </FieldGroup>
    </RecordDrawer>
  )
}

function SchemaFieldEditor({
  field, onChange, onRemove, onUp, onDown,
}: {
  field: SchemaField
  onChange: (patch: Partial<SchemaField>) => void
  onRemove: () => void
  onUp: () => void
  onDown: () => void
}) {
  const [optionsText, setOptionsText] = useState((field.options || []).join(', '))

  useEffect(() => {
    setOptionsText((field.options || []).join(', '))
  }, [field])

  return (
    <div className="bg-muted/30 rounded-lg p-3 border border-border">
      <div className="flex items-center gap-2 mb-2">
        <GripVertical className="h-3 w-3 text-muted-foreground" />
        <div className="flex-1 grid grid-cols-2 gap-2">
          <Input
            placeholder="Field key (e.g. amount)"
            value={field.name}
            onChange={(e) => onChange({ name: e.target.value.replace(/\s+/g, '_').toLowerCase() })}
            className="font-mono text-xs"
          />
          <Input
            placeholder="Label shown to user"
            value={field.label}
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </div>
        <button type="button" onClick={onUp} className="p-1 hover:bg-muted rounded text-muted-foreground text-xs">↑</button>
        <button type="button" onClick={onDown} className="p-1 hover:bg-muted rounded text-muted-foreground text-xs">↓</button>
        <button type="button" onClick={onRemove} className="p-1 hover:bg-destructive/10 rounded text-destructive">
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 items-center">
        <Select value={field.type} onValueChange={(v) => onChange({ type: v as SchemaField['type'] })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {TYPE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="inline-flex items-center gap-1 text-xs">
          <input
            type="checkbox"
            checked={field.required || false}
            onChange={(e) => onChange({ required: e.target.checked })}
          />
          Required
        </label>
        <Input
          placeholder="Placeholder (optional)"
          value={field.placeholder || ''}
          onChange={(e) => onChange({ placeholder: e.target.value })}
        />
      </div>
      {field.type === 'select' && (
        <div className="mt-2">
          <Input
            placeholder="Options (comma-separated)"
            value={optionsText}
            onChange={(e) => {
              setOptionsText(e.target.value)
              onChange({ options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })
            }}
          />
        </div>
      )}
    </div>
  )
}

// ─────────────────── Helpers ───────────────────

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>
      {children}
    </th>
  )
}

function Td({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>
}
